/**
 * Equipment attach/detach lifecycle (E2.2.S2) — keeps the LOCAL player's avatar
 * wearing exactly the meshes for their currently-equipped items, self-view only.
 *
 * `EntityState` carries NO equipment field on the wire (`messages.ts`), so other
 * entities' gear is not client-knowable — this renderer drives ONLY the own
 * avatar. It subscribes (via the caller) to the existing inventory-snapshot store
 * (the one `CharacterPanels.tsx:89` reads), and on each change diffs the equipped
 * set against what is attached:
 *   - attach on join/equip, detach + dispose on unequip/replace,
 *   - never more than one mesh per slot,
 *   - graceful fallback: an unbound item / unknown pack / null template / load
 *     error attaches nothing and raises no error (the loader's own null path).
 *
 * Async is generation-guarded per slot (the SceneBuilder pattern): a load that
 * settles after its slot was detached or re-bound disposes its clone instead of
 * leaking an orphan. Nothing here rides the per-tick path — attaches are keyed
 * off inventory pushes (AD-031), and the meshes carry no collision or stats (EQP
 * §5; the body-invariant 0.4 m collider is never touched).
 *
 * Node-safe like loader.ts: the only `three` dependency is the `Object3D` TYPE
 * (erased at runtime); the loader is injected as a `GltfLoadFn` and the avatar is
 * an abstract `AttachTarget`, so the whole lifecycle runs under `node --test`
 * with fakes (AD-120). Import boundary (AD-122): shared types via
 * `@great-cloud/shared/client`. Loader acquisition + GPU disposal use the shared
 * `PackLoaderCache`/`disposeObject3D` (`../assets`) — the per-class copies were
 * deduped as the E2.2 gate follow-up F3 (2026-07-11).
 */

import type { Object3D } from 'three';

import type { AssetPackDef, ItemWire } from '@great-cloud/shared/client';

import { PackLoaderCache, disposeObject3D, type GltfLoadFn } from '../assets/index.js';
import { fullDetailAssetsActive } from '../scene/assetDetail.js';
import type { PendingAnchor, PendingAssetSink } from '../pendingAssets.js';
import { EQUIPMENT_SOCKETS, applySocket, type SocketTransform } from './sockets.js';
import { resolveEquipment, type EquipmentContent, type SkinPreference } from './resolve.js';

/** The equipped-item fields the renderer consumes (a slice of `ItemWire`). */
export type EquipmentItem = Pick<ItemWire, 'id' | 'contentId' | 'slot'>;

/** Inventory fields needed to select the active character's worn rows. */
export type EquipmentInventoryItem = EquipmentItem & Pick<ItemWire, 'location' | 'locationRef'>;

/**
 * The inventory snapshot is account-wide: `location === 'equipped'` rows for
 * every character share one array and `locationRef` identifies the wearer. A
 * self-view renderer must therefore select BOTH fields or a multi-character
 * account can display a sibling character's first item in each slot.
 */
export function equippedItemsForCharacter<T extends EquipmentInventoryItem>(
  items: readonly T[],
  characterId: number | string,
): T[] {
  const wearer = String(characterId);
  return items.filter((item) => item.location === 'equipped' && item.locationRef === wearer);
}

/** The object the avatar's equipment children are added to/removed from (a `THREE.Group`). */
export interface AttachTarget {
  add(object: Object3D): void;
  remove(object: Object3D): void;
}

/**
 * `userData` key naming the equipment slot that COVERS a wearer node
 * (q-art-handoff-hair-refit-live-head). The rig builder tags the hair wardrobe piece
 * `{ coveredBySlot: 'head' }`; while a mesh is drawn in that slot (a helm) the renderer
 * hides every node so tagged under its target, and shows them again when the slot empties.
 * A node rule, not a mesh rule: the renderer never needs to know what hair is.
 */
export const COVERED_BY_SLOT = 'coveredBySlot';

/** A node `applyCover` can read the tag of and show/hide (duck-typed: no `three` at runtime). */
interface CoverableNode {
  visible?: boolean;
  userData?: Record<string, unknown>;
}

/**
 * Where + how one equipment slot attaches (E2.4 socket re-seat): the parent object
 * the mesh is added to + the local transform written onto it. Capsule mode (the
 * default): parent = the avatar group, transform = the capsule-era `EQUIPMENT_SOCKETS`
 * entry. Rig mode: parent = a resolved CHR-026 §4.2 bone, transform = the bone-local
 * offset (built by `rigSocketPlacement`, E2.4.S2). A real `THREE.Object3D` bone and a
 * test fake both satisfy `AttachTarget`.
 */
export interface SocketPartPlacement {
  /** Named child authored in one animated bone's local bind space. */
  nodeName: string;
  parent: AttachTarget;
  transform: SocketTransform;
}

/**
 * Skinned-armour attach (q-player-armour-visual-fit; TGC-EQP-004's skinned contract). A
 * loaded piece that carries a SkinnedMesh authored on the player rig's own skeleton is
 * re-bound to the WEARER's animated bones (`bind`, true on success) and added to `parent`,
 * so it deforms with the body instead of riding one bone rigidly. `bind` returns false for a
 * rigid piece (no SkinnedMesh) or a skeleton that does not resolve on the wearer; the slot's
 * rigid placement is then used, exactly as before this seam existed.
 */
export interface SkinnedAttach {
  parent: AttachTarget;
  bind(object: Object3D): boolean;
}

export type SocketPlacement =
  | {
      parent: AttachTarget;
      transform: SocketTransform;
      parts?: never;
      skin?: SkinnedAttach;
    }
  | {
      /** One loaded slot split across multiple animated bones at attach time. */
      parts: readonly SocketPartPlacement[];
      parent?: never;
      transform?: never;
      skin?: SkinnedAttach;
    };

/**
 * Resolve where + how an equipment slot attaches, or `undefined` when the slot has
 * no visible socket (`trinket`, an unknown/empty slot — the caller then attaches
 * nothing, the graceful path). The default resolver reproduces E2.2's behaviour (the
 * avatar group + `EQUIPMENT_SOCKETS`); `setPlacement` swaps it for the rig's bone
 * sockets once the CHR-026 rig renders (E2.4.S2).
 */
export type SocketPlacementResolver = (
  slot: string,
  /** Stable model id within the resolved asset pack; permits per-base grip calibration. */
  model: string,
  /**
   * The mesh actually rendered (a per-item mesh or a skin) when it differs from the archetype:
   * a held grip may be declared per mesh (AD-838, `heldGrip.ts`). Absent ⇒ the archetype's.
   */
  mesh?: string,
) => SocketPlacement | undefined;

/**
 * The live equipped set + a change signal — implemented in App from the client
 * store (`equippedItemsForCharacter(state.inventory.items, characterId)` + `subscribe`),
 * so the renderer never imports the store (kept decoupled + node-testable).
 */
export interface EquippedItemsSource {
  /** The items currently equipped by the active character. */
  get(): readonly EquipmentItem[];
  /** Subscribe to changes; returns an unsubscribe function. */
  subscribe(listener: () => void): () => void;
}

/** A tool drawn in the mainhand for the gather beat (`setHeldTool`): its pack and model. */
export interface HeldTool {
  assetPack: string;
  model: string;
}

/** Everything App hands Game so it can drive self-view equipment (all optional to construct). */
export interface EquipmentBridge {
  content: EquipmentContent;
  equipped: EquippedItemsSource;
}

export interface EquipmentRendererOptions {
  /** The avatar group equipment meshes attach to (self-view: Game's `ownMesh`). */
  target: AttachTarget;
  /** Presentation lookup (item presentation + asset packs). */
  content: EquipmentContent;
  /** Injected glTF loader (real: `createThreeGltfLoader()`; tests: a stub). */
  gltfLoad: GltfLoadFn;
  /** Static asset HTTP base for delivery URLs (`assetHttpBase()`); '' = same-origin. */
  httpBase?: string;
  /**
   * Select each entry's full-detail base URL instead of `lods[0]`. Defaults to
   * the session fidelity policy (desktop/full and capture use bases; mobile/low
   * uses proxies); injectable so lifecycle tests pin both paths.
   */
  useFullDetailBase?: boolean;
  /**
   * Where in-flight attaches are reported (q-asset-readiness-loading-screen) and the anchor
   * they report (the wearer). Absent ⇒ unreported.
   */
  pending?: { sink: PendingAssetSink; anchor: PendingAnchor };
  /** Provisional socket map (default `EQUIPMENT_SOCKETS`); injectable for tests. */
  sockets?: Readonly<Record<string, SocketTransform>>;
  /**
   * Per-slot placement resolver (E2.4). Default: the avatar group + the (injected or
   * default) capsule sockets — E2.2 behaviour verbatim. `setPlacement` swaps it for
   * the rig's bone sockets when the CHR-026 rig renders (E2.4.S2 socket re-seat).
   */
  placement?: SocketPlacementResolver;
  /** GPU-resource disposer for a detached mesh (default: three geometry/material dispose). */
  dispose?: (object: Object3D) => void;
  /** Non-fatal warning sink (default `console.warn`). */
  onWarn?: (message: string, error?: unknown) => void;
  /**
   * Event-driven notification when the resolved mainhand changes: its archetype (`model`) and
   * the mesh actually drawn (`mesh`: a per-item mesh or a skin can name its own grip row, and the
   * fist that closes on it; q-grip-fist-inner-hand-see-through).
   */
  onMainhandModelChange?: (model: string | null, mesh: string | null) => void;
  /**
   * Client-local weapon-skin preference (docs/systems/weapon-skins.md): the
   * preferred cosmetic id per slot, consulted on every `sync`/`refresh`. Absent ⇒
   * base meshes only (E2.2 behaviour verbatim). Self-view only, never the wire.
   */
  skinPreference?: SkinPreference;
}

/** Per-slot attachment record (one mesh per slot; async generation-guarded). */
interface SlotRecord {
  /** Identity of the desired attachment (`itemId|pack|model`) — a re-sync no-ops when unchanged. */
  sig: string;
  /**
   * Renderer-wide monotonic id assigned when this attach was kicked off. A load
   * that settles is applied only if its slot still holds THIS id; a stale id (the
   * slot was detached or re-bound while the load was in flight) drops the clone.
   * Monotonic (never per-slot-reset) so a detach+re-attach can't collide.
   */
  generation: number;
  /** The settled attached mesh, or null while loading / on a graceful fallback. */
  object: Object3D | null;
  /** The parent the mesh was attached to (for detach); null while loading / on fallback. */
  parent: AttachTarget | null;
  /** Multipart children re-parented from `object` onto animated bones. */
  parts: Array<{ object: Object3D; parent: AttachTarget }>;
}

/** Duck-typed (no `three` at runtime): does `object` hold a SkinnedMesh anywhere? */
function containsSkinnedMesh(object: Object3D): boolean {
  let found = false;
  object.traverse?.((node) => {
    if ((node as unknown as { isSkinnedMesh?: unknown }).isSkinnedMesh === true) found = true;
  });
  return found;
}

function defaultWarn(message: string, error?: unknown): void {
  console.warn(`[equipment] ${message}`, error ?? '');
}

/**
 * Drives the own avatar's equipment meshes. Create it against the avatar group,
 * `sync()` it with the current equipped set (and on every store change), and
 * `dispose()` it when the avatar is torn down.
 */
export class EquipmentRenderer {
  private readonly target: AttachTarget;
  private readonly content: EquipmentContent;
  private readonly sockets: Readonly<Record<string, SocketTransform>>;
  private readonly disposeObject: (object: Object3D) => void;
  private readonly onWarn: (message: string, error?: unknown) => void;
  private readonly onMainhandModelChange: (model: string | null, mesh: string | null) => void;
  /** The client-local skin preference (undefined ⇒ base meshes only). */
  private readonly skinPreference: SkinPreference | undefined;
  /** Session art-fidelity path; low/mobile remains on the near proxy LOD. */
  private readonly useFullDetailBase: boolean;
  private readonly pending: { sink: PendingAssetSink; anchor: PendingAnchor } | null;
  /** Current per-slot placement resolver (default = the avatar group + capsule sockets). */
  private placement: SocketPlacementResolver;
  /** The default (capsule) placement — restored by `setPlacement(null)`. */
  private readonly defaultPlacement: SocketPlacementResolver;
  /** The last-synced equipped set — re-attached onto a new placement by `setPlacement`. */
  private lastItems: readonly EquipmentItem[] = [];

  /** slot → current attachment. */
  private readonly slots = new Map<string, SlotRecord>();
  /** One `AssetLoader` per pack — the shared cache (E2.2 gate follow-up F3 dedup). */
  private readonly loaders: PackLoaderCache;
  /** Renderer-wide monotonic attach id (see SlotRecord.generation). */
  private nextGeneration = 0;
  private mainhandModel: string | null = null;
  private mainhandMesh: string | null = null;
  /** The gather beat's tool, drawn IN the mainhand for the beat (see `setHeldTool`); null ⇒ none. */
  private heldTool: HeldTool | null = null;
  private disposed = false;

  constructor(options: EquipmentRendererOptions) {
    this.target = options.target;
    this.content = options.content;
    this.sockets = options.sockets ?? EQUIPMENT_SOCKETS;
    this.disposeObject = options.dispose ?? disposeObject3D;
    this.onWarn = options.onWarn ?? defaultWarn;
    this.onMainhandModelChange = options.onMainhandModelChange ?? (() => undefined);
    this.skinPreference = options.skinPreference;
    this.useFullDetailBase = options.useFullDetailBase ?? fullDetailAssetsActive();
    this.pending = options.pending ?? null;
    this.loaders = new PackLoaderCache({
      gltfLoad: options.gltfLoad,
      httpBase: options.httpBase,
      onWarn: this.onWarn,
    });
    // Default placement = the avatar group + the (injected or default) capsule
    // sockets: E2.2's behaviour verbatim, so a renderer built without a `placement`
    // attaches exactly as before. `setPlacement` swaps this for the rig's bones.
    this.defaultPlacement = (slot: string): SocketPlacement | undefined => {
      const transform = this.sockets[slot];
      return transform === undefined ? undefined : { parent: this.target, transform };
    };
    this.placement = options.placement ?? this.defaultPlacement;
  }

  /**
   * Reconcile the avatar's attachments to `items` (the current equipped set):
   * detach slots that are gone or re-bound, attach new/changed ones. Idempotent
   * — an unchanged equipped set (a store bump for some other reason) is a no-op,
   * so nothing reloads or flickers.
   */
  sync(items: readonly EquipmentItem[]): void {
    if (this.disposed) return;
    this.lastItems = items; // remembered so `setPlacement` can re-seat onto a new placement

    // Desired attachment per slot (first resolvable + socketed item wins the slot).
    const desired = new Map<string, string>(); // slot → sig
    const resolutions = new Map<
      string,
      { sig: string; pack: AssetPackDef; model: string; gripModel: string }
    >();
    for (const item of items) {
      const resolution = resolveEquipment(this.content, item, this.skinPreference);
      if (resolution === null) continue; // unbound / no pack ⇒ no attachment (graceful)
      // Placement is keyed by the ARCHETYPE (gripModel): a skin mesh is fitted to
      // its base's grip frame, so it seats with the base's socket calibration.
      if (this.placement(resolution.slot, resolution.gripModel) === undefined) continue; // no socket
      if (desired.has(resolution.slot)) continue; // one mesh per slot (first wins)
      // The skin id is part of the identity so a preference change re-attaches.
      const sig = `${item.id}|${resolution.assetPack}|${resolution.model}|${resolution.skin ?? ''}`;
      desired.set(resolution.slot, sig);
      resolutions.set(resolution.slot, {
        sig,
        pack: resolution.pack,
        model: resolution.model,
        gripModel: resolution.gripModel,
      });
    }

    // The gather beat's tool takes the mainhand for the beat, through the same placement (the hand
    // bone, its held-grip seat) and the same fist signal as an equipped item: one mesh in the hand,
    // never a second copy beside it (q-gather-tool-one-in-hand). A mainhand already drawing that
    // tool (the server's auto-swap equipped it, AD-844) keeps its own attachment.
    const held = this.heldTool;
    if (held !== null && resolutions.get('mainhand')?.model !== held.model) {
      const pack = this.content.assetPack(held.assetPack);
      if (pack !== undefined && this.placement('mainhand', held.model, held.model) !== undefined) {
        const sig = `held-tool|${held.assetPack}|${held.model}`;
        resolutions.set('mainhand', { sig, pack, model: held.model, gripModel: held.model });
        desired.set('mainhand', sig);
      }
    }

    // The grip morph / mainhand signal carries the archetype (the seat) and the rendered mesh (a
    // per-item row picks its fist: q-grip-fist-inner-hand-see-through).
    const nextMainhandModel = resolutions.get('mainhand')?.gripModel ?? null;
    const nextMainhandMesh = resolutions.get('mainhand')?.model ?? null;
    if (nextMainhandModel !== this.mainhandModel || nextMainhandMesh !== this.mainhandMesh) {
      this.mainhandModel = nextMainhandModel;
      this.mainhandMesh = nextMainhandMesh;
      this.onMainhandModelChange(nextMainhandModel, nextMainhandMesh);
    }

    // Detach slots that are gone or whose binding changed.
    for (const [slot, record] of [...this.slots]) {
      if (desired.get(slot) !== record.sig) this.detach(slot);
    }

    // Attach new/changed slots.
    for (const [slot, want] of resolutions) {
      const existing = this.slots.get(slot);
      // Already attached or attaching for this exact binding ⇒ nothing to do.
      if (existing !== undefined && existing.sig === want.sig) continue;
      this.attach(slot, want.sig, want.pack, want.model, want.gripModel);
    }

    this.applyCover(new Set(desired.keys()));
  }

  /**
   * Show/hide the wearer nodes tagged `COVERED_BY_SLOT` (the hair under a helm): hidden
   * while their slot draws a mesh, shown otherwise. Re-applied on every sync (and so on
   * every `setPlacement`), so a rig whose hair lands after the helm is covered on re-seat.
   */
  private applyCover(drawn: ReadonlySet<string>): void {
    const target = this.target as AttachTarget & {
      traverse?: (callback: (node: CoverableNode) => void) => void;
    };
    target.traverse?.((node) => {
      const slot = node.userData?.[COVERED_BY_SLOT];
      if (typeof slot !== 'string') return;
      node.visible = !drawn.has(slot);
    });
  }

  /**
   * Would `cosmeticId` resolve as the skin for what is equipped in `slot` right
   * now? The dev bridge's probe (weapon-skins.md; `installSkinDevBridge`): runs
   * the SAME resolve chain `sync` uses, with the candidate id forced for that
   * slot, and answers only when the resolution carries it as `skin`. Pure — no
   * attach, no store write, no preference change.
   */
  skinApplies(slot: string, cosmeticId: string): boolean {
    if (this.disposed || slot === '' || cosmeticId === '') return false;
    const forced = (s: string): string | null => (s === slot ? cosmeticId : null);
    for (const item of this.lastItems) {
      if (item.slot !== slot) continue;
      const resolution = resolveEquipment(this.content, item, forced);
      if (resolution === null) continue;
      if (this.placement(resolution.slot, resolution.gripModel) === undefined) continue;
      return resolution.skin === cosmeticId; // first socketed item owns the slot (as in sync)
    }
    return false;
  }

  /**
   * Re-run the resolve chain over the last-synced equipped set — the hook a
   * skin-preference change fires (weapon-skins.md). Idempotent like `sync`: only
   * a slot whose resolved identity changed (skin on/off/swapped) re-attaches.
   */
  refresh(): void {
    if (this.disposed) return;
    this.sync(this.lastItems);
  }

  /**
   * Draw a gather tool in the mainhand for the gather beat (E2.2.S3), or `null` to give the hand
   * back to the equipped item. The tool replaces whatever the mainhand draws for the beat and is
   * seated and gripped exactly like an equipped one; when the equipped mainhand already IS that
   * tool, nothing changes. q-gather-tool-one-in-hand: the beat's tool used to be a separate prop on
   * the avatar group at the capsule-era grip, so with the auto-swapped sickle in the hand (AD-844)
   * the owner saw two sickles, one floating above the character.
   */
  setHeldTool(tool: HeldTool | null): void {
    if (this.disposed) return;
    if (tool?.assetPack === this.heldTool?.assetPack && tool?.model === this.heldTool?.model)
      return;
    this.heldTool = tool === null ? null : { assetPack: tool.assetPack, model: tool.model };
    this.sync(this.lastItems);
  }

  /**
   * Switch the socket placement (E2.4 rig re-seat). Detaches the current
   * attachments and re-attaches the last-synced equipped set onto the new placement
   * — so equipment moves from the capsule sockets onto the rig's bones the moment
   * the CHR-026 rig renders. `null` restores the default (avatar group + capsule
   * sockets), i.e. today's E2.2 behaviour. A no-op after `dispose()`.
   */
  setPlacement(resolver: SocketPlacementResolver | null): void {
    if (this.disposed) return;
    this.placement = resolver ?? this.defaultPlacement;
    for (const slot of [...this.slots.keys()]) this.detach(slot);
    this.sync(this.lastItems);
  }

  /** Detach + dispose every attachment and drop the loader caches. */
  /** Slots whose mesh has settled on the avatar (capture-readiness; the scene dump reads it). */
  attachedCount(): number {
    let n = 0;
    for (const record of this.slots.values()) if (record.object !== null) n += 1;
    return n;
  }

  dispose(): void {
    this.disposed = true;
    if (this.mainhandModel !== null) {
      this.mainhandModel = null;
      this.mainhandMesh = null;
      this.onMainhandModelChange(null, null);
    }
    for (const slot of [...this.slots.keys()]) this.detach(slot);
    this.applyCover(new Set()); // nothing drawn any more ⇒ nothing covered
    this.loaders.clear();
  }

  private attach(
    slot: string,
    sig: string,
    pack: AssetPackDef,
    model: string,
    gripModel: string = model,
  ): void {
    // A renderer-wide monotonic id: any still-in-flight prior load is now stale
    // and drops its clone when it settles (guarded in the .then below).
    const generation = ++this.nextGeneration;
    const record: SlotRecord = { sig, generation, object: null, parent: null, parts: [] };
    this.slots.set(slot, record);

    const loader = this.loaders.loaderFor(pack);
    const load = loader.instantiatePreferred(model, this.useFullDetailBase);
    const pending = this.pending
      ? this.pending.sink.track(`equipment: ${model}`, this.pending.anchor, load)
      : load;
    void pending
      .then((object) => {
        const current = this.slots.get(slot);
        if (current === undefined || current.generation !== generation || this.disposed) {
          // Superseded (detached / re-bound / renderer gone) ⇒ no orphan.
          if (object !== null) this.disposeObject(object);
          return;
        }
        if (object === null) return; // graceful: unknown model / no handle / load error ⇒ no attach
        // Resolve placement at settle time — it may have re-seated onto the rig's
        // bones while the load was in flight (E2.4). A slot that lost its socket
        // meanwhile ⇒ no attach (dispose the clone, graceful). Keyed by the
        // archetype so a fitted skin seats exactly where its base seats.
        const place = this.placement(slot, gripModel, model);
        if (place === undefined) {
          this.disposeObject(object);
          return;
        }
        // A skinned piece binds to the wearer's skeleton and deforms with it; a rigid
        // piece (or a skeleton the wearer lacks) falls through to the rigid placement.
        if (place.skin !== undefined && place.skin.bind(object)) {
          place.skin.parent.add(object);
          current.object = object;
          current.parent = place.skin.parent;
          return;
        }
        // A skinned piece nothing bound (the capsule before the rig settles, or a skeleton
        // the wearer lacks) is never attached rigidly: it would stand in its bind pose, and
        // its own armature — bones named exactly like the body's — would sit on the avatar
        // where a bone lookup could find it instead of the body's.
        if (containsSkinnedMesh(object)) {
          this.disposeObject(object);
          return;
        }
        if (place.parts !== undefined) {
          const resolvedParts = place.parts.map((part) => ({
            placement: part,
            object: object.getObjectByName(part.nodeName),
          }));
          const missing = resolvedParts.find((part) => part.object === undefined);
          if (missing !== undefined) {
            this.onWarn(
              `attach '${model}' (${slot}) missing multipart node '${missing.placement.nodeName}'`,
            );
            this.disposeObject(object);
            return;
          }
          for (const part of resolvedParts) {
            const child = part.object as Object3D;
            child.removeFromParent();
            applySocket(child, part.placement.transform);
            part.placement.parent.add(child);
            current.parts.push({ object: child, parent: part.placement.parent });
          }
          // Retain the now-empty loaded root as the single disposal owner. Detach
          // re-homes every part under it before one recursive dispose.
          current.object = object;
          return;
        }
        // A three.js Object3D structurally satisfies SocketTarget (position/rotation/scale).
        applySocket(object, place.transform);
        place.parent.add(object);
        current.object = object;
        current.parent = place.parent;
      })
      .catch((error: unknown) => {
        // The loader already degrades load failures to null; this guards against
        // an unexpected throw so a bad attach never breaks the render loop.
        this.onWarn(`attach '${model}' (${slot}) failed`, error);
      });
  }

  private detach(slot: string): void {
    const record = this.slots.get(slot);
    if (record === undefined) return;
    this.slots.delete(slot);
    if (record.object !== null && record.parts.length > 0) {
      for (const part of record.parts) {
        part.parent.remove(part.object);
        record.object.add(part.object);
      }
      this.disposeObject(record.object);
    } else if (record.object !== null && record.parent !== null) {
      record.parent.remove(record.object);
      this.disposeObject(record.object);
    }
    // If still loading (object null), the generation guard drops the pending clone.
  }
}
